import React, { useEffect, useRef, useState } from "react";
import { getStaffShifts } from "../services/shifts.js";
import { Cards } from "../components/Cards.tsx";
import { Link } from "react-router-dom";
import { capitalize, formatUKTime, toUKWallTime } from "../utils/functions.js";
import { getConfig } from "../services/util.js";
import { BasicSelect } from "../components/Forms/BasicSelect.js";
import DateFilter from "../components/Forms/DateFilter.js";
import Spinner from "../components/PageLoader.js";

export const FieldShifts = () => {
  const [shifts, setShifts] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedServiceUser, setSelectedServiceUser] = useState(null);
  const [filteredShifts, setFilteredShifts] = useState([]);
  const [sortDuration, setSortDuration] = useState({
    label: "Today",
    value: "today",
  });
  const [disableFilterApply, setDisableFilterApply] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const dateFilterRef = useRef();
  const user = JSON.parse(localStorage.getItem("user"));

  const sortOptions = [
    {
      label: "Today",
      value: "today",
    },
    {
      label: "This Week",
      value: "week",
    },
    {
      label: "This Month",
      value: "month",
    },
  ];

  const handleDisableFilterButton = () => {
    if (
      dateRange !== null ||
      dateRange?.startDate === "" ||
      dateRange?.endDate === "" ||
      selectedStaff !== null ||
      selectedServiceUser !== null
    ) {
      setDisableFilterApply(false);
    } else {
      setDisableFilterApply(true);
    }
  };

  const fetchConfig = async () => {
    try {
      const { data } = await getConfig();
      localStorage.setItem("config", JSON.stringify(data));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    handleDisableFilterButton();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStaff, selectedServiceUser, dateRange]);

  const passQuery = async () => {
    const query = {
      shiftStart: dateRange?.startDate,
      shiftEnd: dateRange?.endDate,
      userId: user.username,
    };
    await fetchShifts(query);
    setSortDuration({ label: "Custom", value: "" });
  };

  const sortByNearestDate = (shifts) => {
    const currentDate = new Date();

    return shifts.sort((a, b) => {
      const dateA = new Date(a.shiftStart);
      const dateB = new Date(b.shiftStart);

      // Calculate the absolute difference from the current date
      const diffA = Math.abs(dateA - currentDate);
      const diffB = Math.abs(dateB - currentDate);

      // Sort based on the difference
      return diffA - diffB;
    });
  };

  const fetchShifts = async (query) => {
    setIsLoading(true);
    try {
      const { data } = await getStaffShifts(
        query ?? { userId: user.username || "" }
      );
      setShifts(sortByNearestDate(data));
      fetchConfig();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchShifts();
    // eslint-disable-next-line
  }, []);
  const handleDateFilterChange = (range) => {
    if (range) {
      setDateRange(range);
    } else {
      console.log("Custom range selected, awaiting dates...");
    }
  };

  const clearFilter = () => {
    setSelectedStaff(null);
    setSelectedServiceUser(null);
    setDateRange(null);
    setSortDuration({ label: "Today", value: "today" });
    if (dateFilterRef.current) {
      dateFilterRef.current.handleClearFilter(); // Call the function from the child
    }
  };

  useEffect(() => {
    if (sortDuration && shifts) {
      let sorted;
      // Bucket by UK calendar day so "today"/"week"/"month" mean the same
      // thing on every device, regardless of its timezone
      if (sortDuration.value === "today") {
        sorted = shifts.filter((shift) => {
          const shiftDate = toUKWallTime(shift.shiftStart);
          const today = toUKWallTime(new Date());
          if (!shiftDate) return false;
          const sameDay =
            shiftDate.getDate() === today.getDate() &&
            shiftDate.getMonth() === today.getMonth() &&
            shiftDate.getFullYear() === today.getFullYear();
          return sameDay;
        });
      } else if (sortDuration.value === "week") {
        sorted = shifts.filter((shift) => {
          const shiftDate = toUKWallTime(shift.shiftStart);
          const today = toUKWallTime(new Date());
          if (!shiftDate) return false;
          const sameWeek =
            shiftDate.getFullYear() === today.getFullYear() &&
            shiftDate.getMonth() === today.getMonth() &&
            shiftDate.getDate() >= today.getDate() - 7;
          return sameWeek;
        });
      } else if (sortDuration.value === "month") {
        sorted = shifts.filter((shift) => {
          const shiftDate = toUKWallTime(shift.shiftStart);
          const today = toUKWallTime(new Date());
          if (!shiftDate) return false;
          const sameMonth =
            shiftDate.getFullYear() === today.getFullYear() &&
            shiftDate.getMonth() === today.getMonth();
          return sameMonth;
        });
      } else {
        sorted = shifts;
      }
      setFilteredShifts(sorted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDuration, shifts]);

  return (
    <>
      <div className="d-flex flex-wrap j-c-sb mb-40" style={{ gap: "10px" }}>
        <div className="filter__container">
          <DateFilter
            onFilterChange={handleDateFilterChange}
            ref={dateFilterRef}
          />

          <div className="d-flex">
            <button
              className="btn btn--secondary"
              style={{ height: "37px" }}
              disabled={disableFilterApply}
              onClick={passQuery}
            >
              Apply filter
            </button>
            {!disableFilterApply && (
              <button
                className="btn btn__link ml-4"
                style={{ height: "37px", fontSize: "12px" }}
                onClick={() => {
                  clearFilter();
                  fetchShifts();
                }}
              >
                Clear filter
              </button>
            )}
          </div>
        </div>
        <BasicSelect
          placeholder="Select a date range"
          options={sortOptions}
          name="staff"
          value={sortDuration}
          width="200px"
          onChange={(e) => setSortDuration(e)}
        />
      </div>

      <div className="field-shift">
        {filteredShifts.length > 0 &&
          filteredShifts.map((shift) => (
            <div className="mb-24" key={shift?.shiftId}>
              <h5 className="mb-8">
                {formatUKTime(shift?.shiftStart, "do MMM, yyyy")}
              </h5>
              <Cards classes="field-shift__item">
                <div>
                  <p className="field-shift__name">{`${shift?.serviceUser?.firstName} ${shift?.serviceUser?.lastName}`}</p>
                  <p className="field-shift__time">
                    <span>Start:</span>{" "}
                    {formatUKTime(shift?.shiftStart, "h:mm a")}
                  </p>
                  <p className="field-shift__time">
                    <span>End:</span> {formatUKTime(shift?.shiftEnd, "h:mm a")}
                  </p>
                  <p className="field-shift__time">
                    <span>Status:</span> {capitalize(shift?.status)}
                  </p>
                </div>
                {
                  <Link
                    to={`/field-shifts/${shift?.shiftId}/${shift?.serviceUser?.id}`}
                    className="field-shift__link"
                  >
                    View
                  </Link>
                }
              </Cards>
            </div>
          ))}

        {filteredShifts.length === 0 && !isLoading && (
          <Cards>
            <div className="table__empty">
              <p>No shifts found</p>
            </div>
          </Cards>
        )}

        {isLoading && (
          <div className="documents__loader">
            <Spinner />
          </div>
        )}
      </div>
    </>
  );
};
